import { createFileRoute } from "@tanstack/react-router";
import { ImagePlus, Pencil, Plus, Trash2, RefreshCw } from "lucide-react";
import { useState, type ChangeEvent } from "react";
import { AdminTitle, Field } from "@/components/admin/admin-ui";
import { Button } from "@/components/ui/button";
import { meta } from "@/components/store/page-parts";
import { useStore } from "@/lib/store-context";
import type { Banner } from "@/data/banners";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/banners")({
  head: () => meta("Manage Banners — BetterAccess Admin", "Upload, update, arrange and remove storefront banners."),
  component: BannerAdmin,
});

const blank: Banner = {
  id: "",
  image: "",
  alt: "",
  eyebrow: "Featured",
  title: "",
  copy: "",
  cta: "Shop now",
  href: "/products",
  tone: "dark",
};

function BannerAdmin() {
  const { banners, setBanners } = useStore();
  const [editing, setEditing] = useState<Banner | null>(null);
  const [saving, setSaving] = useState(false);

  const update = (key: keyof Banner, value: string) =>
    setEditing(current => (current ? { ...current, [key]: value } : current));

  const upload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error("Banner image must be under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update("image", String(reader.result ?? ""));
    reader.readAsDataURL(file);
  };

  const persistBanners = async (newList: Banner[]) => {
    setBanners(newList);
    try {
      await fetch("/api/admin/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ store_banners: JSON.stringify(newList) }),
      });
    } catch {
      // ignore
    }
  };

  const save = async () => {
    if (!editing?.title || !editing.image) {
      toast.error("Add a title and banner image");
      return;
    }
    setSaving(true);
    try {
      const item = { ...editing, id: editing.id || `banner-${Date.now()}`, alt: editing.alt || editing.title };
      const updated = banners.some(b => b.id === item.id)
        ? banners.map(b => (b.id === item.id ? item : b))
        : [...banners, item];
      await persistBanners(updated);
      setEditing(null);
      toast.success("Banner saved and published to storefront!");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    const updated = banners.filter(b => b.id !== id);
    await persistBanners(updated);
    toast.success("Banner removed");
  };

  return (
    <>
      <AdminTitle
        eyebrow="Storefront"
        title="Homepage banners"
        copy="Banners rotate automatically on the homepage carousel in the order shown."
        action={
          <Button onClick={() => setEditing(blank)}>
            <Plus />
            New banner
          </Button>
        }
      />

      {editing && (
        <section className="premium-panel mb-6 p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Short label / Badge">
              <input
                value={editing.eyebrow}
                onChange={e => update("eyebrow", e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Headline">
              <input
                value={editing.title}
                onChange={e => update("title", e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Description">
              <textarea
                value={editing.copy}
                onChange={e => update("copy", e.target.value)}
                rows={3}
                className="rounded-md border bg-background p-3 font-normal"
              />
            </Field>
            <Field label="Button text">
              <input
                value={editing.cta}
                onChange={e => update("cta", e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Button link">
              <input
                value={editing.href}
                onChange={e => update("href", e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Text style">
              <select
                value={editing.tone}
                onChange={e => update("tone", e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              >
                <option value="dark">Light text (on dark overlay)</option>
                <option value="light">Dark text (on bright backdrop)</option>
              </select>
            </Field>
          </div>

          <label className="mt-4 flex min-h-28 cursor-pointer items-center justify-center gap-3 rounded-lg border border-dashed bg-secondary/40 p-4 text-sm font-bold hover:border-primary">
            <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={upload} />
            <ImagePlus className="size-5 text-primary" />
            {editing.image ? "Replace banner image" : "Upload banner image (3:1 aspect ratio recommended)"}
          </label>

          {editing.image && (
            <div className="mt-3 overflow-hidden rounded-lg border">
              <img src={editing.image} alt="Preview" className="aspect-[3/1] w-full object-cover" />
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <RefreshCw className="size-4 animate-spin" /> : "Save banner"}
            </Button>
          </div>
        </section>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {banners.map(banner => (
          <article key={banner.id} className="premium-panel overflow-hidden">
            <img src={banner.image} alt={banner.alt} className="aspect-[3/1] w-full object-cover" />
            <div className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="truncate font-bold">{banner.title}</p>
                <p className="mt-1 truncate text-xs text-muted-foreground">{banner.href}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="outline" aria-label={`Edit ${banner.title}`} onClick={() => setEditing(banner)}>
                  <Pencil className="size-4" />
                </Button>
                <Button size="icon" variant="outline" aria-label={`Delete ${banner.title}`} onClick={() => remove(banner.id)}>
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {!banners.length && (
        <div className="rounded-lg border border-dashed py-20 text-center">
          <ImagePlus className="mx-auto size-8 text-muted-foreground" />
          <p className="mt-3 font-bold">No homepage banners</p>
          <p className="mt-1 text-sm text-muted-foreground">Add one to restore the storefront carousel.</p>
        </div>
      )}
    </>
  );
}